import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Loader2, Smartphone, MessageSquare, Hash } from "lucide-react";
import { pushApi } from "@/lib/api";
import {
  isPushSupported,
  getPermission,
  getExistingSubscription,
  enablePush,
  disablePush,
} from "@/lib/push";

export const PushNotificationSettings = ({ toast }) => {
  const [supported] = useState(isPushSupported());
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState(getPermission());
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const sub = await getExistingSubscription();
      const status = await pushApi.getStatus();
      setEnabled(Boolean(sub) && status.data.subscribed);
      setPermission(getPermission());
    } catch (e) {
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (supported) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [supported, refresh]);

  const handleToggle = async (checked) => {
    setWorking(true);
    try {
      if (checked) {
        await enablePush();
        setEnabled(true);
        setPermission("granted");
        toast.success("Benachrichtigungen aktiviert");
      } else {
        await disablePush();
        setEnabled(false);
        toast.success("Benachrichtigungen deaktiviert");
      }
    } catch (e) {
      toast.error(e.message || "Aktion fehlgeschlagen");
      setPermission(getPermission());
    } finally {
      setWorking(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await pushApi.sendTest();
      toast.success(res.data.message || "Test gesendet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="w-5 h-5" />
            Push-Benachrichtigungen
          </CardTitle>
          <CardDescription>
            Dieser Browser unterstützt keine Push-Benachrichtigungen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 p-4 rounded-lg text-sm text-muted-foreground flex gap-3">
            <Smartphone className="w-5 h-5 shrink-0 mt-0.5" />
            <span>
              Auf dem iPhone funktionieren Push-Benachrichtigungen nur, wenn die App über Safari
              („Teilen → Zum Home-Bildschirm") als App installiert wurde (iOS 16.4+). Auf Android &amp;
              Desktop-Chrome funktioniert es direkt.
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const blocked = permission === "denied";

  return (
    <Card data-testid="push-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Push-Benachrichtigungen
        </CardTitle>
        <CardDescription>
          Erhalte Benachrichtigungen direkt auf dieses Gerät – auch wenn die App nicht geöffnet ist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border">
              <div className="min-w-0">
                <p className="font-medium">Benachrichtigungen auf diesem Gerät</p>
                <p className="text-sm text-muted-foreground">
                  {blocked
                    ? "Im Browser blockiert – bitte in den Website-Einstellungen erlauben."
                    : enabled
                    ? "Aktiv – du erhältst Push-Benachrichtigungen."
                    : "Aktiviere den Schalter, um Benachrichtigungen zu erhalten."}
                </p>
              </div>
              <Switch
                checked={enabled}
                disabled={working || blocked}
                onCheckedChange={handleToggle}
                data-testid="push-toggle"
              />
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
              <p className="font-medium">Du wirst benachrichtigt bei:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Neuen Team-Chat-Nachrichten
                </li>
                <li className="flex items-center gap-2">
                  <Hash className="w-4 h-4" /> Ablaufenden Seriennummern (tägliche Erinnerung um 08:00 Uhr)
                </li>
              </ul>
            </div>

            {enabled && (
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing}
                className="gap-2"
                data-testid="push-test-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Test-Benachrichtigung senden
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
